import React, { useContext, useEffect, useMemo, useState } from 'react';
import ContextProvider from '@app/hooks/useQuestionario';

import {
  Button,
  EditableTable,
  FormatText,
  FormDatePicker,
  FormInput,
  FormSelect,
  FormSubmitButton,
  FormTextArea,
  MasterDetailForm,
  Row,
  Table,
  usePromise,
  Widget,
} from '@digital/ui';
import { Collapse, Icon, Tooltip } from 'antd';
import {
  AbaGovernancaAprovacao,
  avancarGovernanca,
  downloadAnexoGovernanca,
  getAtributosCciOptions,
  getDadosGovernanca,
  updateGovernanca,
  viewAnexoGovernanca,
  reprovarGovernanca,
  aprovarGovernanca,
} from '@app/api/governancaAprovacao';
import { getVendorList, excluirVendor, Vendor } from '@app/api/sas/ordemCompra';
import CurrencyInput from '@app/components/CurrencyInput';
import {optionsSwitch} from '@app/helpers/optionsSwitch';
import ModalIncluirVendor from './ModalIncluirVendor';
import ModalIncluirAnexo from './ModalIncluirAnexo';
import { GetFileExtension, imageFormats } from '@app/helpers';
import { hasPermission } from '@app/helpers/permissions';

const AbaGovernanca = () => {
  const { id, requisicaoCompraId, ordemCompraId, governancaAprovacaoId, update, setUpdate } = useContext(
    ContextProvider,
  );
  const [dadosGovernanca, setDadosGovernanca] = useState<AbaGovernancaAprovacao>();
  const [vendorList, setVendorList] = useState<Vendor[]>([]);
  const [disableSumario, setDisableSumario] = useState<boolean>(true);

  const onUpdate = () => {
    setUpdate(!update);
  };

  const redirectSumario = (link: string) => {
    window.open(link, '_blank');
  };

  const handleSubmit = (values: any) => {
    updateGovernanca({
      ...values,
      id: dadosGovernanca.id,
      demandaId: dadosGovernanca.demandaId,
    }).then(data => {
      if (data.valid) {
        onUpdate();
      }
    });
  };

  const handleExcluirVendor = (id: string) => {
    excluirVendor(id).then(data => {
      onUpdate();
    });
  };

  const handleAvancar = values => {
    updateGovernanca({
      ...values,
      id: dadosGovernanca.id,
      demandaId: dadosGovernanca.demandaId,
    }).then(data => {
      if (data.valid) {
        avancarGovernanca(dadosGovernanca.id).then(data => {
          if (data.valid) {
            onUpdate();
          }
        });
      }
    });
  };

  const handleAprovar = () => {
    aprovarGovernanca(dadosGovernanca.id).then(data => {
      if (data.valid) {
        onUpdate();
      }
    });
  };

  const handleReprovar = () => {
    reprovarGovernanca(dadosGovernanca.id).then(data => {
      if (data.valid) {
        onUpdate();
      }
    });
  };

  useEffect(() => {
    if (id) {
      getDadosGovernanca(id).then(data => {
        setDadosGovernanca(data);
      });
    }
  }, [id, update]);

  useEffect(() => {
    if (dadosGovernanca) {
      if (dadosGovernanca.ordemCompraId) {
        getVendorList(dadosGovernanca.ordemCompraId).then(data => {
          if (data) {
            setVendorList(data);
          }
        });
      }
    }
  }, [dadosGovernanca]);

  const vendorListColumns = useMemo(() => {
    const columns = [];

    columns.push({
      title: 'código',
      dataIndex: 'codigoTotvs',
      width: '10%',
    });

    columns.push({
      title: 'Cnpj',
      dataIndex: 'cnpj',
      width: '15%',
    });

    columns.push({
      title: 'fornecedor',
      dataIndex: 'nome',
    });

    columns.push({
      title: 'Ações',
      width: '5%',
      render: (text, record) => (
        <>
          {hasPermission(['VENDOR_DELETAR']) && (
            <Tooltip title="Excluir">
              <Button icon="science-minus" onClick={() => handleExcluirVendor(record.id)} />
            </Tooltip>
          )}
          {hasPermission(['VENDOR_EDITAR']) && <ModalIncluirVendor data={record} />}
        </>
      ),
    });

    return columns;
  }, [vendorList]);

  const anexosColumns = useMemo(() => {
    const columns = [];

    columns.push({
      title: 'descrição',
      dataIndex: 'descricao',
      width: '40%',
    });

    columns.push({
      title: 'arquivo',
      dataIndex: 'nome',
    });

    columns.push({
      title: 'Ações',
      width: '5%',
      render: (text, record) => (
        <>
          <ModalIncluirAnexo dados={record} />
          {record && record.local && (
            <>
              <Tooltip title="Download Arquivo">
                <Button
                  icon="ui-download"
                  type="link"
                  onClick={() => {
                    downloadAnexoGovernanca(record.id, record.nome);
                  }}
                />
              </Tooltip>
              {GetFileExtension(record.nome) === 'pdf' || imageFormats.includes(GetFileExtension(record.nome)) ? (
                <Tooltip title="Visualizar Arquivo">
                  <Button
                    icon="files-view_file"
                    type="link"
                    onClick={() => {
                      viewAnexoGovernanca(record.id, record.nome);
                    }}
                  />
                </Tooltip>
              ) : (
                <></>
              )}
            </>
          )}
        </>
      ),
    });

    return columns;
  }, [dadosGovernanca]);

  const actions = useMemo(() => {
    const actions = [];

    if (dadosGovernanca) {
      if (
        dadosGovernanca.statusGovernancaAprovacaoCodigo === 4 &&
        hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_AVANCAR_SUPRIMENTOS'])
      ) {
        setDisableSumario(false);
        actions.push(
          <Button
            type="link"
            target="_blank"
            onClick={() => redirectSumario(`/demanda/sumarioExecutivo/${dadosGovernanca.demandaId}`)}>
            Sumário Executivo
          </Button>,
        );
        actions.push(
          <FormSubmitButton noIcon onClick={values => handleSubmit(values)}>
            <FormatText id="ui.commons.save" uppercase />
          </FormSubmitButton>,
        );
      }

      if (
        (dadosGovernanca.statusGovernancaAprovacaoCodigo === 1 ||
          dadosGovernanca.statusGovernancaAprovacaoCodigo === 3 ||
          dadosGovernanca.statusGovernancaAprovacaoCodigo === 5) &&
        hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_AVANCAR_PRE_SUPRIMENTOS'])
      ) {
        setDisableSumario(false);
        actions.push(
          <Button
            type="link"
            target="_blank"
            onClick={() => redirectSumario(`/demanda/sumarioExecutivo/${dadosGovernanca.demandaId}`)}>
            Sumário Executivo
          </Button>,
        );
        actions.push(
          <FormSubmitButton noIcon onClick={values => handleSubmit(values)}>
            <FormatText id="ui.commons.save" uppercase />
          </FormSubmitButton>,
        );
        actions.push(
          <FormSubmitButton noIcon onClick={values => handleAvancar(values)}>
            Avançar
          </FormSubmitButton>,
        );
      }

      if (
        (dadosGovernanca.statusGovernancaAprovacaoCodigo === 2 ||
          dadosGovernanca.statusGovernancaAprovacaoCodigo === 4) &&
        hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_AVANCAR_SUPRIMENTOS'])
      ) {
        actions.push(
          <FormSubmitButton noIcon onClick={values => handleAvancar(values)}>
            Avançar
          </FormSubmitButton>,
        );
      }

      if (
        dadosGovernanca.statusGovernancaAprovacaoCodigo === 6 &&
        hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_APROVAR_REPROVAR'])
      ) {
        actions.push(
          <FormSubmitButton noIcon onClick={values => handleSubmit(values)}>
            <FormatText id="ui.commons.save" uppercase />
          </FormSubmitButton>,
        );
        actions.push(
          <Button type="danger" onClick={handleReprovar}>
            Reprovar
          </Button>,
        );
        actions.push(<Button onClick={handleAprovar}>Aprovar</Button>);
      }
    }
    return actions;
  }, [dadosGovernanca]);

  const { Panel } = Collapse;
  return (
    <>
      {dadosGovernanca ? (
        <Collapse
          bordered={false}
          defaultActiveKey={'governancaAprovacao'}
          expandIcon={() => <Icon type="arrows-right_4" />}>
          <Panel key="governancaAprovacao" header={<span>{dadosGovernanca.identificador}</span>}>
            <MasterDetailForm initialValues={dadosGovernanca}>
              <Widget actions={actions}>
                <Row>
                  <FormInput label="Identificação" name="identificador" span={4} disabled />
                  <FormInput label="Status" name="statusGovernancaAprovacaoTexto" span={4} disabled />
                  <FormInput label="Responsável Dep. Pré-Contratação" name="responsavelGpc" span={4} disabled />
                </Row>
                <Row>
                  <CurrencyInput label="Valor Final Contratação" id='mrn-input-expectativaValorFinal' name="expectativaValorFinal" span={4} disabled />
                  <FormSelect
                    label="Previsão Orçamentária?"
                    name="temPrevisaoOrcamentaria"
                    options={optionsSwitch}
                    span={4}
                    disabled
                  />
                  <FormSelect
                    label="Fornecedor Direcionado?"
                    name="isFornecedorDirecionado"
                    options={optionsSwitch}
                    span={4}
                    disabled
                  />
                </Row>

                <fieldset>
                  <legend>Vendor List</legend>
                  <Row>
                    {hasPermission(['VENDOR_EDITAR']) && dadosGovernanca.statusGovernancaAprovacaoCodigo == 2 && (
                      <ModalIncluirVendor />
                    )}
                    <Table columns={vendorListColumns} dataSource={vendorList} />
                  </Row>
                </fieldset>

                {dadosGovernanca.statusGovernancaAprovacaoCodigo >= 1 &&
                  dadosGovernanca.statusGovernancaAprovacaoCodigo != 7 && (
                    <fieldset>
                      <legend>CCI - Índice de Complexidade do Contrato</legend>
                      <Row>
                        <FormSelect
                          allowClear
                          getOptions={getAtributosCciOptions('Escopo')}
                          label="Escopo"
                          name="cciEscopoId"
                          span={6}
                          disabled={
                            ![1, 2, 3].includes(dadosGovernanca.statusGovernancaAprovacaoCodigo) ||
                            !hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_AVANCAR_PRE_SUPRIMENTOS'])
                          }
                        />
                        <FormSelect
                          allowClear
                          getOptions={getAtributosCciOptions('Cláusula de Saída')}
                          label="Cláusula de Saída"
                          name="cciClausulaSaidaId"
                          span={6}
                          disabled={
                            ![1, 2, 3].includes(dadosGovernanca.statusGovernancaAprovacaoCodigo) ||
                            !hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_AVANCAR_PRE_SUPRIMENTOS'])
                          }
                        />
                      </Row>
                      <Row>
                        <FormSelect
                          allowClear
                          getOptions={getAtributosCciOptions('Conflito de Interesse')}
                          label="Conflito de Interesse"
                          name="cciConflitoInteresseId"
                          span={6}
                          disabled={
                            ![1, 2, 3].includes(dadosGovernanca.statusGovernancaAprovacaoCodigo) ||
                            !hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_AVANCAR_PRE_SUPRIMENTOS'])
                          }
                        />
                        <FormSelect
                          allowClear
                          getOptions={getAtributosCciOptions('Contratação')}
                          label="Direcionamento"
                          name="cciDirecionamentoId"
                          span={6}
                          disabled={
                            ![1, 2, 3].includes(dadosGovernanca.statusGovernancaAprovacaoCodigo) ||
                            !hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_AVANCAR_PRE_SUPRIMENTOS'])
                          }
                        />
                      </Row>
                      <Row>
                        <FormInput label="Pontuação CCI" name="cciPontuacao" span={4} disabled />
                      </Row>
                      <Row>
                        <FormInput label="Regra Definida" name="cciRegraDefinida" span={4} disabled />
                      </Row>
                      <Row>
                        <FormTextArea
                          label="Resumo Governança de Aprovação"
                          name="cciResumo"
                          span={12}
                          rows={1}
                          disabled
                        />
                      </Row>
                    </fieldset>
                  )}

                {dadosGovernanca.statusGovernancaAprovacaoCodigo >= 4 &&
                  dadosGovernanca.statusGovernancaAprovacaoCodigo != 7 && (
                    <fieldset>
                      <legend>Sumário Executivo - Suprimentos</legend>
                      <Row>
                        <FormInput
                          disabled={disableSumario}
                          label="Contractor"
                          name="sumarioExecutivo.contractor"
                          span={4}
                        />
                        <FormInput
                          disabled={disableSumario}
                          label="Area & Department"
                          name="sumarioExecutivo.areaDepartment"
                          span={4}
                        />
                        <FormInput
                          disabled={disableSumario}
                          label="Total Value"
                          name="sumarioExecutivo.totalValue"
                          span={4}
                        />
                      </Row>
                      <Row>
                        <FormInput
                          disabled={disableSumario}
                          label="Contract Number"
                          name="sumarioExecutivo.contract"
                          span={4}
                        />
                        <FormInput
                          disabled={disableSumario}
                          label="Commencement"
                          name="sumarioExecutivo.commencement"
                          span={4}
                        />
                        <FormInput
                          disabled={disableSumario}
                          label="Termination"
                          name="sumarioExecutivo.termination"
                          span={4}
                        />
                      </Row>
                      <Row>
                        <FormInput
                          disabled={disableSumario}
                          label="Vendor List"
                          name="sumarioExecutivo.vendorList"
                          span={2}
                        />
                        <FormInput
                          disabled={disableSumario}
                          label="Sub Techinical"
                          name="sumarioExecutivo.subTechinical"
                          span={2}
                        />
                        <FormInput
                          disabled={disableSumario}
                          label="Sub. Commercial"
                          name="sumarioExecutivo.subCommercial"
                          span={2}
                        />
                        <FormInput disabled={disableSumario} label="Max." name="sumarioExecutivo.max" span={2} />
                        <FormInput disabled={disableSumario} label="Min." name="sumarioExecutivo.min" span={2} />
                        <FormInput
                          disabled={disableSumario}
                          label="Selected"
                          name="sumarioExecutivo.selected"
                          span={2}
                        />
                      </Row>
                    </fieldset>
                  )}

                {dadosGovernanca.statusGovernancaAprovacaoCodigo >= 3 &&
                  dadosGovernanca.statusGovernancaAprovacaoCodigo != 7 && (
                    <fieldset>
                      <legend>Anexos</legend>
                      <Row>
                        {dadosGovernanca.statusGovernancaAprovacaoCodigo == 4 &&
                          hasPermission(['PRE_SUPRIMENTOS_DEMANDA_ABA_GOVERNANCA_AVANCAR_SUPRIMENTOS']) && (
                            <ModalIncluirAnexo dados={undefined} />
                          )}
                        <Table columns={anexosColumns} dataSource={dadosGovernanca.anexos} />
                      </Row>
                    </fieldset>
                  )}

                {dadosGovernanca.statusGovernancaAprovacaoCodigo >= 6 &&
                  dadosGovernanca.statusGovernancaAprovacaoCodigo != 7 && (
                    <fieldset>
                      <legend>Aprovação BoD</legend>
                      <Row>
                        <FormDatePicker
                          label="Data Envio E-mail para BoD"
                          name="dataEnvioEmailBod"
                          format="DD/MM/YYYY"
                          span={4}
                        />
                        <FormDatePicker
                          label="Data Limite Aprovação BoD"
                          name="dataLimiteAprovacaoBod"
                          format="DD/MM/YYYY"
                          span={4}
                          disabled
                        />
                      </Row>
                    </fieldset>
                  )}
              </Widget>
            </MasterDetailForm>
          </Panel>
        </Collapse>
      ) : (
        <></>
      )}
    </>
  );
};
export default AbaGovernanca;
